// Circuit de validation (CV-3) : « À décider », la vue d'une instance et la décision par un geste, le suivi des
// instances (en cours et historique), « Mes alertes », la délégation et l'absence, et, pour l'administration du
// circuit, la configuration, la simulation « qui validerait ? », la suspension d'urgence et la levée.
// Règles (fiche du circuit, §§ 3.8 à 3.10, 4.5, 4.7, 4.9) : une décision ne se prend qu'ici, par un geste sur la clé
// de sécurité, décision choisie à la main (aucune n'est présélectionnée, aucune n'est lue dans l'URL) ; refuser ou
// demander une modification exige un motif ; tout texte vient de l'API et s'affiche en texte brut.
import { lireManques } from '../domaine/manques';
import { ReserveCalcul } from '../composants/ManquesCalcul';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { ClientEkera } from '../api/client';
import type { AssertionWebAuthn, ContenuSoumisVue, PlanEcriture } from '../api/types';
import { Attendre, Erreur, Etiquette } from '../composants/communs';
import {
  avisEnTete,
  delegationDe,
  etapesOrdonnees,
  etapesPourMoi,
  fichierAttestation,
  LIBELLE_AVIS,
  LIBELLE_DECISION,
  LIBELLE_ENVOI,
  LIBELLE_ETAT,
  LIBELLE_ROLE,
  LIBELLE_TYPE,
  motifRequis,
  type ADecider,
  type DecisionEtape,
  type DefiCircuit,
  type InstanceVue,
  type ValeurAvis,
} from '../domaine/circuits';
import { dateFr, eurosLong } from '../domaine/format';
import { telecharger } from '../domaine/telechargement';
import { gesteAssertion } from '../domaine/webauthn';
import { ValeurChamp } from './Documents';

export type OngletCircuit = 'a-decider' | 'suivi' | 'alertes' | 'delegation' | 'configuration';

export interface ProprietesCircuits {
  client: ClientEkera;
  /** Sujet de la personne connectée (pour dire « vous » et proposer le retrait à l'auteur). */
  sujet: string;
  emetteur?: string | null;
  /** Rôle d'accès administration du client : configuration, suspension, levée. */
  administrateur: boolean;
  /** La personne lit la carte (rôle d’accès). Sinon, rien de la carte (CV-S11) ; l’objet exact soumis reste montré (CV3I-1). */
  lecteurCarte: boolean;
  onglet: OngletCircuit;
  instance: string | null;
  /** Aller vers un onglet ou une instance (l'URL change ; aucune décision n'y est jamais portée). */
  aller: (o: { onglet?: OngletCircuit; instance?: string | null }) => void;
}

const ONGLETS: { id: OngletCircuit; libelle: string; admin?: boolean }[] = [
  { id: 'a-decider', libelle: 'À décider' },
  { id: 'suivi', libelle: 'Instances et historique' },
  { id: 'alertes', libelle: 'Mes alertes' },
  { id: 'delegation', libelle: 'Délégation et absence' },
  { id: 'configuration', libelle: 'Configuration', admin: true },
];

/** Défi, geste de la personne, puis l'acte : ni le défi ni l'assertion ne sont gardés ailleurs que dans cette fonction. */
async function avecGeste<T>(defi: () => Promise<DefiCircuit>, acte: (defiId: string, assertion: AssertionWebAuthn) => Promise<T>): Promise<T> {
  const d = await defi();
  const assertion = await gesteAssertion(d.options);
  return acte(d.defi_id, assertion);
}

export function EcranCircuits(p: ProprietesCircuits) {
  const onglets = ONGLETS.filter((o) => !o.admin || p.administrateur);
  return (
    <div className="pile" data-testid="ecran-circuits">
      <p className="note">
        Chaque décision se prend ici, dans Ekera, par un geste sur votre clé de sécurité. Un courriel d’Ekera ne fait que
        prévenir : il ne contient jamais de bouton pour décider, ni de code à saisir.
      </p>
      <div className="onglets" role="group" aria-label="Circuit de validation">
        {onglets.map((o) => (
          <button key={o.id} type="button" aria-pressed={!p.instance && p.onglet === o.id} onClick={() => p.aller({ onglet: o.id, instance: null })}>
            {o.libelle}
          </button>
        ))}
      </div>
      {p.instance ? (
        <VueInstanceCircuit {...p} iid={p.instance} />
      ) : p.onglet === 'suivi' ? (
        <Suivi {...p} />
      ) : p.onglet === 'alertes' ? (
        <MesAlertes {...p} />
      ) : p.onglet === 'delegation' ? (
        <DelegationAbsence {...p} />
      ) : p.onglet === 'configuration' && p.administrateur ? (
        <Configuration {...p} />
      ) : (
        <BoiteADecider {...p} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- « À décider »

function BoiteADecider({ client, aller }: ProprietesCircuits) {
  const q = useQuery({ queryKey: ['circuits', 'a-decider'], queryFn: () => client.aDecider() });
  return (
    <section className="panneau pile" aria-labelledby="t-a-decider">
      <h2 id="t-a-decider">À décider</h2>
      <Attendre etats={[q]} quoi="les décisions attendues">
        {() =>
          q.data!.length === 0 ? (
            <p data-testid="rien-a-decider">Aucune décision ne vous attend.</p>
          ) : (
            <ul className="liste" data-testid="liste-a-decider">
              {q.data!.map((x) => (
                <li key={`${x.instance}:${x.etape}`}>
                  <button type="button" className="lien-btn" onClick={() => aller({ instance: x.instance })}>
                    {LIBELLE_TYPE[x.type_objet] ?? x.type_objet}, référence <span className="num">{x.reference}</span>
                  </button>{' '}
                  — étape « {x.libelle} »{delegationDe(x.par) ? ' (par délégation)' : ''}
                  {x.auteur ? ' · vous en êtes l’auteur' : ''}
                  {x.echeance ? ` · échéance ${dateFr(x.echeance)}` : ''}
                </li>
              ))}
            </ul>
          )
        }
      </Attendre>
    </section>
  );
}

// ---------------------------------------------------------------- vue d'une instance et décision

function VueInstanceCircuit({ client, iid, sujet, aller }: ProprietesCircuits & { iid: string }) {
  const qc = useQueryClient();
  const vue = useQuery({ queryKey: ['circuits', 'instance', iid], queryFn: () => client.instanceCircuit(iid), retry: false });
  const boite = useQuery({ queryKey: ['circuits', 'a-decider'], queryFn: () => client.aDecider() });
  const relire = () => {
    void qc.invalidateQueries({ queryKey: ['circuits'] });
  };
  return (
    <Attendre etats={[vue, boite]} quoi="l’instance du circuit">
      {() => {
        const v = vue.data!;
        const miennes = etapesPourMoi(boite.data!, iid);
        const auteur = (v as InstanceVue & { auteurs?: { sub: string | null }[] }).auteurs?.some((a) => a.sub === sujet) ?? false;
        const lisible = objetLisible(v);
        return (
          <div className="pile" data-testid="instance-circuit">
            <section className="panneau pile" aria-labelledby="t-instance">
              <h2 id="t-instance">
                {LIBELLE_TYPE[v.type_objet] ?? v.type_objet}, référence <span className="num">{v.reference}</span>
              </h2>
              <dl className="paires">
                <dt>État</dt>
                <dd>
                  <Etiquette ton={v.etat === 'refusee' || v.etat === 'caduque' ? 'grave' : v.etat === 'en_cours' ? 'attention' : 'ok'}>
                    {LIBELLE_ETAT[v.etat] ?? v.etat}
                  </Etiquette>
                  {v.motif ? <> — {v.motif}</> : null}
                </dd>
                <dt>Empreinte de l’objet</dt>
                <dd className="num" data-testid="empreinte-objet">
                  {v.empreinte_objet}
                </dd>
                {v.ouverte_le && (
                  <>
                    <dt>Soumis le</dt>
                    <dd>{dateFr(v.ouverte_le)}</dd>
                  </>
                )}
                {v.echeance && (
                  <>
                    <dt>Échéance</dt>
                    <dd>{dateFr(v.echeance)}</dd>
                  </>
                )}
                {v.auteur_admis && (
                  <>
                    <dt>Dérogation</dt>
                    <dd>L’auteur peut compter parmi les approbateurs (« validé par son auteur » sera dit).</dd>
                  </>
                )}
              </dl>
              <ObjetExact v={v} />
            </section>
            <Etapes v={v} />
            {miennes.length > 0 && v.etat === 'en_cours' && !lisible && (
              <p className="encadre grave" role="alert" data-testid="decision-impossible">
                L’objet exact soumis ne peut pas être montré ici : vous ne pouvez pas vous prononcer sur une empreinte seule.
                Prévenez l’auteur ou un administrateur du circuit.
              </p>
            )}
            {miennes.length > 0 && v.etat === 'en_cours' && lisible && (
              <section className="panneau pile" aria-labelledby="t-decider">
                <h2 id="t-decider">Votre décision</h2>
                {miennes.map((m) => (
                  <FormDecision key={m.etape} client={client} v={v} m={m} etape={v.etapes.find((e) => e.id === m.etape)} surFait={relire} />
                ))}
              </section>
            )}
            {v.attestation && <Attestation client={client} iid={iid} />}
            {auteur && (v.etat === 'en_cours' || v.etat === 'approuvee') && <Retrait client={client} iid={iid} surFait={relire} />}
            <p>
              <button type="button" className="lien-btn" onClick={() => aller({ onglet: 'a-decider', instance: null })}>
                Revenir à « À décider »
              </button>
            </p>
          </div>
        );
      }}
    </Attendre>
  );
}

function Champ({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <>
      <dt>{titre}</dt>
      <dd>{children}</dd>
    </>
  );
}

const texte = (x: unknown): string => (x === null || x === undefined ? '—' : typeof x === 'string' ? x : JSON.stringify(x));

/** Vrai si l'écran peut montrer l'objet exact que la clé approuve : sans lui, aucune décision n'est proposée. */
function objetLisible(v: InstanceVue): boolean {
  if (v.type_objet === 'publication_livrable') return !!v.contenu_soumis && v.contenu_actuel !== false;
  if (v.type_objet === 'ecriture_outil') return Array.isArray((v.objet_detail?.plan as PlanEcriture | undefined)?.actions);
  return true;
}

/** La version exacte du livrable soumise (CV3I-1), en lecture seule : ce que l'empreinte du contenu couvre. */
function ContenuSoumis({ c }: { c: ContenuSoumisVue }) {
  const reste = c.liste_a_completer.length;
  return (
    <section className="pile" data-testid="contenu-soumis" aria-labelledby="t-contenu-soumis">
      <h3 id="t-contenu-soumis">Version soumise, en lecture seule : {c.titre}</h3>
      <p className="note">
        Version {c.version} du document daté du {c.date_document}, empreinte du contenu <span className="num">{c.empreinte_contenu}</span>.
        C’est exactement ce que votre clé approuve.
      </p>
      {c.sections.map((s) => (
        <div key={s.id} className="pile" data-testid={`soumis-section-${s.id}`}>
          {s.niveau ? <h4>{s.titre}</h4> : null}
          {s.champs.map((ch) => (
            <div key={ch.id} className={ch.etat === 'a_completer' ? 'doc-champ a-completer' : 'doc-champ'}>
              <div className="doc-libelle">{ch.libelle}</div>
              <ValeurChamp c={ch} />
            </div>
          ))}
        </div>
      ))}
      {reste > 0 && (
        <p className="note">
          {reste} champ{reste > 1 ? 's' : ''} reste{reste > 1 ? 'nt' : ''} à compléter dans cette version.
        </p>
      )}
    </section>
  );
}

/** Les champs d'un objet, chacun en texte brut (jamais interprété). */
function ChampsBruts({ donnees, testid }: { donnees: Record<string, unknown> | undefined | null; testid?: string }) {
  const paires = Object.entries(donnees ?? {});
  if (paires.length === 0) return <p className="note">Aucun champ.</p>;
  return (
    <dl className="paires" data-testid={testid}>
      {paires.map(([k, x]) => (
        <Champ key={k} titre={k}>
          <span className="texte-brut">{texte(x)}</span>
        </Champ>
      ))}
    </dl>
  );
}

/** L'objet exact soumis : ce que l'empreinte couvre (§ 4.9), selon le type. */
function ObjetExact({ v }: { v: InstanceVue }) {
  const o = v.objet_detail ?? {};
  if (v.type_objet === 'publication_livrable') {
    const d = (o.destination ?? {}) as Record<string, unknown>;
    return (
      <div className="pile" data-testid="objet-publication">
        {v.contenu_actuel === false && (
          <p className="encadre grave" role="alert" data-testid="contenu-change">
            Le livrable a changé depuis la soumission : la version montrée ici est celle qui a été soumise, pas le contenu
            actuel. Cette instance ne peut plus aboutir ; l’auteur doit soumettre la nouvelle version.
          </p>
        )}
        <dl className="paires">
          <Champ titre="Type de document (fixé par la configuration)">{texte(o.type_protege ?? 'non classé : plancher de l’homologation')}</Champ>
          <Champ titre="Empreinte du contenu">{<span className="num">{texte(o.empreinte_contenu)}</span>}</Champ>
          <Champ titre="Destination">
            {texte(d.outil)} · {texte(d.instance)} · {texte(d.espace)} / {texte(d.dossier)} / {texte(d.nom_fichier)}
          </Champ>
        </dl>
        {o.desaccord_type === true && (
          <p className="encadre attention" role="alert">
            Désaccord de type : le gabarit propose « {texte(o.type_propose)} ».
            Le circuit applique le type de la configuration validée : « {texte(o.type_protege ?? 'non classé : plancher de l’homologation')} ».
          </p>
        )}
        {v.contenu_soumis ? (
          <ContenuSoumis c={v.contenu_soumis} />
        ) : (
          <p className="encadre attention" data-testid="contenu-soumis-absent">
            La version soumise n’est pas lisible ici : l’empreinte ne suffit pas pour se prononcer.
          </p>
        )}
      </div>
    );
  }
  if (v.type_objet === 'ecriture_outil') {
    const plan = (o.plan ?? null) as PlanEcriture | null;
    if (!plan || !Array.isArray(plan.actions))
      return (
        <p className="encadre attention" data-testid="objet-ecriture">
          Le plan d’écriture n’est pas lisible ici : l’empreinte ne suffit pas pour se prononcer.
        </p>
      );
    const versions = Object.entries(plan.versions_lues ?? {});
    return (
      <div className="pile" data-testid="objet-ecriture">
        <dl className="paires">
          <Champ titre="Outil">{`${texte(o.logiciel ?? plan.logiciel)} (${texte(o.instance_logiciel)})`}</Champ>
          <Champ titre="Nature">{o.nature === 'annulation' ? 'Annulation (opération compensatoire)' : 'Écriture'}</Champ>
          <Champ titre="Table de correspondance">{texte(plan.table)}</Champ>
          <Champ titre="Demande">
            {texte(plan.demande.nature)} · clé <span className="num">{texte(plan.demande.cle)}</span>
          </Champ>
          <Champ titre="Cible dans l’outil">
            <span data-testid="ecriture-cible">{plan.demande.cible ? texte(plan.demande.cible) : 'aucune : un nouvel objet sera créé'}</span>
            {plan.demande.version_lue !== undefined && plan.demande.version_lue !== null ? ` (version lue ${texte(plan.demande.version_lue)})` : ''}
          </Champ>
        </dl>
        <h3>Contenu de la demande</h3>
        <ChampsBruts donnees={plan.demande.contenu} testid="ecriture-contenu" />
        <h3>Actions, telles qu’elles seront envoyées</h3>
        <ol className="liste" data-testid="ecriture-actions">
          {plan.actions.map((a, i) => (
            <li key={i} className="pile" data-testid={`ecriture-action-${i}`}>
              <div>
                <b>{texte(a.nature)}</b> · type <span className="num">{texte(a.type_logiciel)}</span> · identifiant{' '}
                <span className="num">{a.identifiant ? texte(a.identifiant) : 'nouvel objet'}</span> — {texte(a.motif)}
              </div>
              <ChampsBruts donnees={a.donnees} testid={`ecriture-donnees-${i}`} />
            </li>
          ))}
        </ol>
        <h3>Versions lues dans l’outil</h3>
        {versions.length === 0 ? (
          <p className="note" data-testid="ecriture-versions">Aucune version lue (création).</p>
        ) : (
          <dl className="paires" data-testid="ecriture-versions">
            {versions.map(([k, x]) => (
              <Champ key={k} titre={k}>
                <span className="num">{texte(x)}</span>
              </Champ>
            ))}
          </dl>
        )}
      </div>
    );
  }
  if (v.type_objet === 'decision_variante') {
    const m = v.montant;
    const contenu = o.contenu_decision as Record<string, unknown> | undefined;
    const fiche = contenu?.fiche as Record<string, unknown> | undefined;
    // Le nom vient de la fiche soumise, jamais de la carte actuelle ; la liste d’exclusion reste celle des chiffres.
    const nomsSoumis = new Map(lireManques(fiche?.manques).map((x) => [`composant:${x.objet}`, x.nom]));
    const manquesSoumis = lireManques((o.variante_calculee as Record<string, unknown> | undefined)?.manques)
      .map((x) => ({ ...x, nom: x.nom ?? nomsSoumis.get(x.objet) }));
    return (
      <div className="pile" data-testid="objet-decision">
        <ReserveCalcul manques={manquesSoumis} titre="Périmètre soumis — points à confirmer avant de prendre une décision" />
        <p className="note">Le montant et les réserves ci-dessus décrivent le périmètre soumis. Le recalcul ci-dessous décrit son écart avec le périmètre courant.</p>
        <dl className="paires">
          <Champ titre="Montant retenu pour le circuit">
            {m && m.calculable !== false && typeof m.valeur === 'number' ? `${eurosLong(m.valeur)} HT` : 'non calculable : toutes les étapes de montant s’appliquent'}
          </Champ>
          {v.recalcul && (
            <Champ titre="Recalcul à la fin du circuit">
              {v.recalcul.caduque ? `caduque : ${(v.recalcul.motifs ?? []).join(' ; ')}` : `écart de ${eurosLong(v.recalcul.ecart_montant ?? 0)}`}
            </Champ>
          )}
        </dl>
        {(v.corrections_par_membres ?? []).length > 0 && (
          <p className="encadre attention" data-testid="corrections-membres">
            Des membres de ce circuit ont corrigé des éléments utilisés par ce scénario depuis la proposition d’Ekera :{' '}
            {(v.corrections_par_membres ?? []).map((c) => c.element).join(', ')}.
          </p>
        )}
        <details>
          <summary>Fiche de décision et chiffres du moteur, tels que soumis</summary>
          <pre className="bloc-code">{JSON.stringify({ fiche: o.contenu_decision, chiffres: o.variante_calculee }, null, 1)}</pre>
        </details>
      </div>
    );
  }
  return (
    <details data-testid="objet-autre">
      <summary>Objet soumis</summary>
      <pre className="bloc-code">{JSON.stringify(o, null, 1)}</pre>
    </details>
  );
}

function Etapes({ v }: { v: InstanceVue }) {
  const voix = avisEnTete(v.voix ?? []);
  return (
    <section className="panneau pile" aria-labelledby="t-etapes">
      <h2 id="t-etapes">Étapes et raisons</h2>
      <ol className="liste" data-testid="etapes">
        {etapesOrdonnees(v.etapes).map((e) => (
          <li key={e.id} data-testid={`etape-${e.id}`}>
            <b>{e.libelle}</b> ({e.nature === 'avis' ? 'avis' : 'approbation'}, rang {e.rang}){' '}
            {e.terminee ? <Etiquette ton="ok">terminée</Etiquette> : e.bloquee ? <Etiquette ton="grave">bloquée</Etiquette> : e.ouverte ? <Etiquette ton="attention">ouverte</Etiquette> : <Etiquette ton="neutre">à venir</Etiquette>}
            {e.bloquee && e.motif_blocage ? <> — {e.motif_blocage}</> : null}
            {(e.raisons ?? []).length > 0 && <div className="note">Pourquoi : {(e.raisons ?? []).join(' ; ')}</div>}
          </li>
        ))}
      </ol>
      <h3>Décisions et avis donnés</h3>
      {voix.length === 0 ? (
        <p>Personne ne s’est encore prononcé.</p>
      ) : (
        <ul className="liste" data-testid="voix">
          {voix.map((x, i) => (
            <li key={i}>
              {dateFr(x.le)} · {x.personne?.nom ?? x.personne?.sub ?? 'une personne'} · étape {x.etape} :{' '}
              {LIBELLE_DECISION[x.decision] ?? x.decision}
              {x.avis ? ` (${LIBELLE_AVIS[x.avis] ?? x.avis})` : ''}
              {x.de_l_auteur ? ' · avis de l’auteur' : ''}
              {x.par_delegation ? ' · par délégation' : ''}
              {x.tombee ? ` · voix tombée${x.motif_tombee ? ` (${x.motif_tombee})` : ''}` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FormDecision({
  client,
  v,
  m,
  etape,
  surFait,
}: {
  client: ClientEkera;
  v: InstanceVue;
  m: ADecider;
  etape: InstanceVue['etapes'][number] | undefined;
  surFait: () => void;
}) {
  const avis = etape?.nature === 'avis';
  const [decision, setDecision] = useState<DecisionEtape | null>(null);
  const [valeurAvis, setValeurAvis] = useState<ValeurAvis | null>(null);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [fait, setFait] = useState<string | null>(null);
  const choix: { d: DecisionEtape; libelle: string }[] = avis
    ? [{ d: 'avis', libelle: 'Donner un avis' }]
    : [
        { d: 'approuve', libelle: 'Approuver' },
        { d: 'refuse', libelle: 'Refuser' },
        { d: 'modification_demandee', libelle: 'Demander une modification' },
      ];
  const manqueMotif = decision !== null && motifRequis(decision) && motif.trim() === '';
  const pret = decision !== null && !manqueMotif && (!avis || valeurAvis !== null) && !envoi;
  async function confirmer() {
    if (!decision) return;
    setErreur(null);
    setEnvoi(true);
    try {
      const delegation_id = delegationDe(m.par);
      // CV3I-3 : le motif est dans le défi ; le même texte part avec la décision, et le champ est figé pendant le geste
      const corps = { decision, avis: avis ? valeurAvis : null, empreinte_objet: v.empreinte_objet, delegation_id, motif: motif.trim() || null };
      await avecGeste(
        () => client.defiDecision(v.id, m.etape, corps),
        (defi_id, assertion) => client.decider(v.id, m.etape, { ...corps, defi_id, assertion }),
      );
      setFait(decision);
      surFait();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }
  if (fait)
    return (
      <p className="encadre ok" role="status" data-testid="decision-faite">
        Votre décision ({LIBELLE_DECISION[fait] ?? fait}) est signée et inscrite au journal du circuit.
      </p>
    );
  return (
    <form className="pile" onSubmit={(e) => { e.preventDefault(); void confirmer(); }} data-testid={`decision-${m.etape}`}>
      <p>
        Étape « {m.libelle} »{delegationDe(m.par) ? ', par délégation' : ''}. Vous vous prononcez sur l’objet d’empreinte{' '}
        <span className="num">{v.empreinte_objet.slice(0, 16)}…</span> ; si l’objet change, une nouvelle instance sera nécessaire.
      </p>
      <fieldset>
        <legend>Décision</legend>
        {choix.map((c) => (
          <label key={c.d} className="ligne">
            <input type="radio" name={`decision-${m.etape}`} checked={decision === c.d} disabled={envoi} onChange={() => setDecision(c.d)} /> {c.libelle}
          </label>
        ))}
      </fieldset>
      {avis && (
        <fieldset>
          <legend>Avis</legend>
          {(['favorable', 'reserve', 'defavorable'] as ValeurAvis[]).map((a) => (
            <label key={a} className="ligne">
              <input type="radio" name={`avis-${m.etape}`} checked={valeurAvis === a} disabled={envoi} onChange={() => setValeurAvis(a)} /> {LIBELLE_AVIS[a]}
            </label>
          ))}
        </fieldset>
      )}
      <label className="pile">
        Motif {decision && motifRequis(decision) ? '(obligatoire)' : '(facultatif)'}
        <textarea value={motif} maxLength={2000} rows={3} readOnly={envoi} aria-readonly={envoi} data-testid={`motif-${m.etape}`} onChange={(e) => { if (!envoi) setMotif(e.target.value); }} />
      </label>
      {manqueMotif && <p className="note" role="status">Un motif est obligatoire pour refuser ou demander une modification.</p>}
      {!!erreur && <Erreur erreur={erreur} titre="La décision n’a pas été enregistrée : rien n’est signé." />}
      <p>
        <button type="submit" className="btn sombre" disabled={!pret}>
          {envoi ? 'Touchez votre clé de sécurité…' : 'Confirmer par un geste sur ma clé'}
        </button>
      </p>
    </form>
  );
}

function Attestation({ client, iid }: { client: ClientEkera; iid: string }) {
  const [erreur, setErreur] = useState<unknown>(null);
  async function charger() {
    setErreur(null);
    try {
      const att = await client.attestationCircuit(iid);
      const f = fichierAttestation(iid, att);
      telecharger(new Blob([f.contenu], { type: 'application/json' }), f.nom);
    } catch (e) {
      setErreur(e);
    }
  }
  return (
    <section className="panneau pile" aria-labelledby="t-attestation">
      <h2 id="t-attestation">Attestation de fin de circuit</h2>
      <p>Signée par Ekera, elle dit qui a approuvé quoi et quand ; elle ne sert qu’une fois, pour l’exécution.</p>
      <p>
        <button type="button" className="btn" onClick={() => void charger()}>
          Télécharger l’attestation (JSON)
        </button>
      </p>
      {!!erreur && <Erreur erreur={erreur} />}
    </section>
  );
}

function Retrait({ client, iid, surFait }: { client: ClientEkera; iid: string; surFait: () => void }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  async function retirer() {
    setErreur(null);
    setEnvoi(true);
    try {
      const m = motif.trim() || null;
      await avecGeste(
        () => client.defiActe('retrait', iid, { motif: m }),
        (defi_id, assertion) => client.retirerInstance(iid, { motif: m, defi_id, assertion }),
      );
      surFait();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }
  return (
    <section className="panneau pile" aria-labelledby="t-retrait">
      <h2 id="t-retrait">Retirer l’objet du circuit</h2>
      <p>Vous en êtes l’auteur. Le retrait se fait par un geste ; les personnes qui se sont déjà prononcées sont prévenues.</p>
      <label className="pile">
        Motif (facultatif)
        <input value={motif} maxLength={500} onChange={(e) => setMotif(e.target.value)} />
      </label>
      {!!erreur && <Erreur erreur={erreur} titre="Le retrait n’a pas été fait." />}
      <p>
        <button type="button" className="btn" disabled={envoi} onClick={() => void retirer()}>
          Retirer par un geste sur ma clé
        </button>
      </p>
    </section>
  );
}

// ---------------------------------------------------------------- instances en cours et historique

function Suivi({ client, aller }: ProprietesCircuits) {
  // L'historique est paginé par l'API (« suivant ») : « Voir plus » lit la page suivante, jamais tout d'un coup.
  const q = useInfiniteQuery({
    queryKey: ['circuits', 'historique'],
    queryFn: ({ pageParam }) => client.historiqueCircuit(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.suivant ?? undefined,
  });
  const instances = q.data?.pages.flatMap((pg) => pg.instances) ?? [];
  return (
    <section className="panneau pile" aria-labelledby="t-suivi">
      <h2 id="t-suivi">Instances en cours et historique</h2>
      <Attendre etats={[{ isPending: q.isPending, error: q.isFetchNextPageError ? null : q.error }]} quoi="les instances du circuit">
        {() => (
          <>
            <p className="note">{q.data!.pages[0]?.tout ? 'Toutes les instances de votre entreprise.' : 'Les instances que vous avez soumises ou où vous vous êtes prononcé.'}</p>
            {instances.length === 0 ? (
              <p>Aucune instance.</p>
            ) : (
              <table className="tableau" data-testid="historique">
                <thead>
                  <tr>
                    <th scope="col">Référence</th>
                    <th scope="col">Type</th>
                    <th scope="col">État</th>
                    <th scope="col">Soumise le</th>
                    <th scope="col">Dernier changement</th>
                  </tr>
                </thead>
                <tbody>
                  {instances.map((x) => (
                    <tr key={x.instance}>
                      <td>
                        <button type="button" className="lien-btn num" onClick={() => aller({ instance: x.instance })}>
                          {x.reference}
                        </button>
                      </td>
                      <td>
                        {LIBELLE_TYPE[x.type_objet] ?? x.type_objet}
                        {x.objet ? <div className="note">{x.objet}</div> : null}
                      </td>
                      <td>
                        {LIBELLE_ETAT[x.etat] ?? x.etat}
                        {x.motif ? <div className="note">{x.motif}</div> : null}
                      </td>
                      <td>{dateFr(x.ouverte_le)}</td>
                      <td>{dateFr(x.mise_a_jour_le)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {q.hasNextPage && (
              <p>
                <button type="button" className="btn" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()} data-testid="historique-plus">
                  {q.isFetchingNextPage ? 'Lecture…' : 'Voir plus'}
                </button>
              </p>
            )}
            {q.isFetchNextPageError && <Erreur erreur={q.error} titre="Impossible de lire la page suivante." />}
          </>
        )}
      </Attendre>
    </section>
  );
}

// ---------------------------------------------------------------- mes alertes

function MesAlertes({ client, aller }: ProprietesCircuits) {
  const q = useQuery({ queryKey: ['circuits', 'alertes'], queryFn: () => client.alertesCircuit() });
  return (
    <section className="panneau pile" aria-labelledby="t-alertes">
      <h2 id="t-alertes">Mes alertes</h2>
      <p className="note">
        Les alertes du circuit sont toujours visibles ici, qu’un courriel soit parti ou non. Ekera ne vous demandera jamais
        de décider par courriel.
      </p>
      <Attendre etats={[q]} quoi="vos alertes">
        {() =>
          q.data!.alertes.length === 0 ? (
            <p>Aucune alerte ces 90 derniers jours.</p>
          ) : (
            <ul className="liste" data-testid="alertes">
              {q.data!.alertes.map((a) => (
                <li key={a.id} className="pile" style={{ gap: 4 }}>
                  <b>{a.sujet}</b>
                  <span>{a.texte}</span>
                  <span className="note">
                    {dateFr(a.cree_le)} · {a.pour === 'administrateurs' ? 'aux administrateurs' : 'pour vous'} · {LIBELLE_ENVOI[a.envoi] ?? a.envoi}
                    {a.instance ? (
                      <>
                        {' · '}
                        <button type="button" className="lien-btn" onClick={() => aller({ instance: a.instance! })}>
                          Ouvrir l’instance
                        </button>
                      </>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )
        }
      </Attendre>
    </section>
  );
}

// ---------------------------------------------------------------- délégation et absence

/** « 2026-11-24 » (champ date) → ISO, fin de journée en UTC. */
function finDeJour(date: string): string {
  return `${date}T23:59:00+00:00`;
}

function DelegationAbsence({ client, emetteur }: ProprietesCircuits) {
  const qc = useQueryClient();
  const moi = useQuery({ queryKey: ['circuits', 'moi'], queryFn: () => client.circuitsMoi() });
  const [role, setRole] = useState('');
  const [sub, setSub] = useState('');
  const [emetteurDelegataire, setEmetteurDelegataire] = useState(emetteur ?? '');
  const [fin, setFin] = useState('');
  const [finAbsence, setFinAbsence] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const relire = () => void qc.invalidateQueries({ queryKey: ['circuits'] });
  async function faire(f: () => Promise<unknown>, ok: string) {
    setErreur(null);
    setMessage(null);
    try {
      await f();
      setMessage(ok);
      relire();
    } catch (e) {
      setErreur(e);
    }
  }
  return (
    <Attendre etats={[moi]} quoi="votre situation dans le circuit">
      {() => {
        const m = moi.data!;
        return (
          <div className="pile">
            <section className="panneau pile" aria-labelledby="t-situation">
              <h2 id="t-situation">Votre situation</h2>
              <p data-testid="roles-circuit">
                Rôles de circuit : {m.roles_circuit.length ? m.roles_circuit.map((r) => LIBELLE_ROLE[r] ?? r).join(', ') : 'aucun'}
                {m.suspendu ? ' · vous êtes suspendu du circuit' : ''}
                {m.absent ? ' · absence déclarée' : ''}
              </p>
              {m.delegations_donnees.length > 0 && (
                <ul className="liste" data-testid="delegations-donnees">
                  {m.delegations_donnees.map((d) => (
                    <li key={d.delegation}>
                      Votre voix « {LIBELLE_ROLE[d.role] ?? d.role} » est déléguée jusqu’au {dateFr(d.fin)}
                      {d.terminee_le ? ` (terminée le ${dateFr(d.terminee_le)})` : (
                        <>
                          {' '}
                          <button type="button" className="lien-btn" onClick={() => void faire(() => client.finDelegation(d.delegation), 'Délégation terminée.')}>
                            Y mettre fin
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {m.delegations_recues.length > 0 && (
                <ul className="liste" data-testid="delegations-recues">
                  {m.delegations_recues.map((d) => (
                    <li key={d.delegation}>
                      Vous tenez par délégation le rôle « {LIBELLE_ROLE[d.role] ?? d.role} » jusqu’au {dateFr(d.fin)}.
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="panneau pile" aria-labelledby="t-deleguer">
              <h2 id="t-deleguer">Déléguer ma voix</h2>
              <p className="note">30 jours au plus, non transitive, jamais à l’auteur de l’instance ; réservée aux rôles tenus par des personnes désignées. Vous y mettez fin à tout moment, sans geste.</p>
              <label className="pile">
                Rôle
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="">Choisir…</option>
                  {m.roles_circuit.filter((r) => r !== 'administration_circuit').map((r) => (
                    <option key={r} value={r}>
                      {LIBELLE_ROLE[r] ?? r}
                    </option>
                  ))}
                </select>
              </label>
              <label className="pile">
                Identifiant de la personne (fourni par votre fournisseur d’identité)
                <input value={sub} onChange={(e) => setSub(e.target.value)} />
              </label>
              <label className="pile">
                Émetteur de son identité
                <input value={emetteurDelegataire} onChange={(e) => setEmetteurDelegataire(e.target.value)} />
              </label>
              <label className="pile">
                Jusqu’au
                <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
              </label>
              <p>
                <button
                  type="button"
                  className="btn"
                  disabled={!role || !sub.trim() || !emetteurDelegataire.trim() || !fin}
                  onClick={() =>
                    void faire(() => {
                      const parametres = { emetteur: emetteurDelegataire.trim(), sub: sub.trim(), role, debut: new Date().toISOString(), fin: finDeJour(fin), type_objet: null };
                      return avecGeste(() => client.defiActe('delegation', null, parametres), (defi_id, assertion) => client.deleguer({ parametres, defi_id, assertion }));
                    }, 'Délégation créée ; vous et la personne êtes alertés.')
                  }
                >
                  Déléguer par un geste sur ma clé
                </button>
              </p>
            </section>
            <section className="panneau pile" aria-labelledby="t-absence">
              <h2 id="t-absence">Déclarer une absence</h2>
              <p className="note">Vous n’êtes plus sollicité jusqu’à cette date ; vos instances passent à votre délégataire ou aux suppléants. Aucune voix déjà donnée n’est retirée.</p>
              <label className="pile">
                Absent jusqu’au
                <input type="date" value={finAbsence} onChange={(e) => setFinAbsence(e.target.value)} />
              </label>
              <p>
                <button
                  type="button"
                  className="btn"
                  disabled={!finAbsence}
                  onClick={() =>
                    void faire(() => {
                      const parametres = { fin: finDeJour(finAbsence) };
                      return avecGeste(() => client.defiActe('absence', null, parametres), (defi_id, assertion) => client.declarerAbsence({ parametres, defi_id, assertion }));
                    }, 'Absence déclarée et alertée.')
                  }
                >
                  Déclarer par un geste sur ma clé
                </button>
              </p>
            </section>
            {message && (
              <p className="encadre ok" role="status">
                {message}
              </p>
            )}
            {!!erreur && <Erreur erreur={erreur} titre="Rien n’a été enregistré." />}
          </div>
        );
      }}
    </Attendre>
  );
}

// ---------------------------------------------------------------- configuration (administration du circuit)

interface VersionVue {
  version: number;
  etat: string;
  nature?: string | null;
  classement?: unknown;
  proposee_le?: string | null;
  approuvee_le?: string | null;
  activable_le?: string | null;
  annulee_le?: string | null;
  instance?: string | null;
}

function Configuration({ client, aller }: ProprietesCircuits) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['circuits', 'configuration'], queryFn: () => client.configurationCircuit() });
  const [texteConfig, setTexteConfig] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [sim, setSim] = useState<{ type_objet: string; carte: string; objet: string }>({ type_objet: 'decision_variante', carte: '', objet: '' });
  const [resultatSim, setResultatSim] = useState<Record<string, unknown> | null>(null);
  const [susp, setSusp] = useState({ emetteur: '', sub: '', motif: '' });
  const [levee, setLevee] = useState({ suspension: '', demande: '' });
  const relire = () => void qc.invalidateQueries({ queryKey: ['circuits'] });
  async function faire(f: () => Promise<unknown>, ok: string) {
    setErreur(null);
    setMessage(null);
    try {
      await f();
      setMessage(ok);
      relire();
    } catch (e) {
      setErreur(e);
    }
  }
  return (
    <Attendre etats={[q]} quoi="la configuration du circuit">
      {() => {
        const c = q.data! as { active: Record<string, unknown> | null; version_active: number | null; versions: VersionVue[]; defaut: Record<string, unknown> };
        const base = texteConfig ?? JSON.stringify(c.active ?? c.defaut, null, 1);
        const maintenant = new Date().toISOString();
        return (
          <div className="pile">
            <section className="panneau pile" aria-labelledby="t-versions">
              <h2 id="t-versions">Versions de la configuration</h2>
              <p data-testid="version-active">{c.version_active ? `Version en vigueur : ${c.version_active}.` : 'Aucune version en vigueur : rien ne peut être soumis au circuit.'}</p>
              <table className="tableau" data-testid="versions">
                <thead>
                  <tr>
                    <th scope="col">Version</th>
                    <th scope="col">État</th>
                    <th scope="col">Classement</th>
                    <th scope="col">En vigueur à partir du</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {c.versions.map((v) => {
                    const enCarence = v.etat === 'approuvee' && !!v.activable_le && v.activable_le > maintenant && !v.annulee_le;
                    return (
                      <tr key={v.version}>
                        <td>{v.version}</td>
                        <td>{v.annulee_le ? 'annulée' : v.etat}</td>
                        <td>{v.nature ?? '—'}</td>
                        <td>{v.activable_le ? dateFr(v.activable_le) : '—'}{enCarence ? ' (carence en cours)' : ''}</td>
                        <td>
                          {v.instance && v.etat !== 'approuvee' && (
                            <button type="button" className="lien-btn" onClick={() => aller({ instance: v.instance! })}>
                              Voir l’instance
                            </button>
                          )}
                          {enCarence && (
                            <button type="button" className="lien-btn" onClick={() => void faire(() => client.annulerCarence(v.version, null), `Version ${v.version} annulée pendant sa carence.`)}>
                              Annuler (sans geste)
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
            <section className="panneau pile" aria-labelledby="t-proposer">
              <h2 id="t-proposer">Proposer une nouvelle version</h2>
              <p className="note">
                Une version est approuvée par un autre administrateur du circuit, avec son geste. Un durcissement s’applique
                aussitôt, y compris aux instances ouvertes ; un assouplissement attend sa carence (24 heures), annulable par
                tout administrateur ou membre touché. Le code refuse toute version sous ses planchers.
              </p>
              <label className="pile">
                Configuration (format du circuit, JSON)
                <textarea rows={14} className="num" value={base} onChange={(e) => setTexteConfig(e.target.value)} />
              </label>
              <p>
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    void faire(async () => {
                      let configuration: Record<string, unknown>;
                      try {
                        configuration = JSON.parse(base) as Record<string, unknown>;
                      } catch {
                        throw new Error('La configuration n’est pas un JSON lisible : rien n’a été proposé.');
                      }
                      return avecGeste(() => client.defiActe('proposition', null, configuration), (defi_id, assertion) => client.proposerConfiguration({ configuration, defi_id, assertion }));
                    }, 'Version proposée : elle attend l’approbation d’un autre administrateur du circuit.')
                  }
                >
                  Proposer par un geste sur ma clé
                </button>
              </p>
            </section>
            <section className="panneau pile" aria-labelledby="t-simulation">
              <h2 id="t-simulation">Qui validerait cet objet ? Qui serait prévenu ?</h2>
              <div className="ligne">
                <label>
                  Type{' '}
                  <select value={sim.type_objet} onChange={(e) => setSim({ ...sim, type_objet: e.target.value })}>
                    <option value="decision_variante">Décision sur un scénario</option>
                    <option value="ecriture_outil">Écriture dans un outil (plan gardé)</option>
                  </select>
                </label>
                <label>
                  Carte <input value={sim.carte} onChange={(e) => setSim({ ...sim, carte: e.target.value })} />
                </label>
                <label>
                  {sim.type_objet === 'decision_variante' ? 'Décision' : 'Plan'} <input value={sim.objet} onChange={(e) => setSim({ ...sim, objet: e.target.value })} />
                </label>
                <button
                  type="button"
                  className="btn"
                  disabled={!sim.carte || !sim.objet}
                  onClick={() =>
                    void faire(async () => {
                      const d = sim.type_objet === 'decision_variante' ? { type_objet: sim.type_objet, carte: sim.carte, objet: sim.objet } : { type_objet: sim.type_objet, carte: sim.carte, plan_id: sim.objet };
                      setResultatSim(await client.simulationCircuit(d));
                    }, 'Simulation faite : rien n’a été ouvert.')
                  }
                >
                  Simuler
                </button>
              </div>
              {resultatSim && <Simulation r={resultatSim} />}
            </section>
            <section className="panneau pile" aria-labelledby="t-suspension">
              <h2 id="t-suspension">Suspension d’urgence d’un approbateur</h2>
              <p className="note">Immédiate et alertée à tous les administrateurs ; ses voix non exécutées tombent. La levée se fait à quatre yeux.</p>
              <div className="ligne">
                <label>
                  Émetteur <input value={susp.emetteur} onChange={(e) => setSusp({ ...susp, emetteur: e.target.value })} />
                </label>
                <label>
                  Identifiant <input value={susp.sub} onChange={(e) => setSusp({ ...susp, sub: e.target.value })} />
                </label>
                <label>
                  Motif <input value={susp.motif} maxLength={500} onChange={(e) => setSusp({ ...susp, motif: e.target.value })} />
                </label>
                <button
                  type="button"
                  className="btn"
                  disabled={!susp.emetteur || !susp.sub}
                  onClick={() =>
                    void faire(() => {
                      const parametres = { emetteur: susp.emetteur.trim(), sub: susp.sub.trim(), motif: susp.motif.trim() || null };
                      return avecGeste(() => client.defiActe('suspension', null, parametres), (defi_id, assertion) => client.suspendre({ parametres, defi_id, assertion }));
                    }, 'Personne suspendue ; ses voix non exécutées sont tombées.')
                  }
                >
                  Suspendre par un geste sur ma clé
                </button>
              </div>
              <div className="ligne">
                <label>
                  Suspension à lever <input value={levee.suspension} onChange={(e) => setLevee({ ...levee, suspension: e.target.value })} />
                </label>
                <button
                  type="button"
                  className="btn"
                  disabled={!levee.suspension}
                  onClick={() =>
                    void faire(() => {
                      const parametres = { motif: null };
                      return avecGeste(() => client.defiActe('levee', levee.suspension, parametres), (defi_id, assertion) => client.demanderLevee(levee.suspension, { parametres, defi_id, assertion }));
                    }, 'Levée demandée : un autre administrateur du circuit doit l’approuver.')
                  }
                >
                  Demander la levée
                </button>
                <label>
                  Demande de levée à approuver <input value={levee.demande} onChange={(e) => setLevee({ ...levee, demande: e.target.value })} />
                </label>
                <button
                  type="button"
                  className="btn"
                  disabled={!levee.demande}
                  onClick={() =>
                    void faire(() => avecGeste(() => client.defiActe('levee_approbation', levee.demande, {}), (defi_id, assertion) => client.approuverLevee(levee.demande, { defi_id, assertion })), 'Suspension levée.')
                  }
                >
                  Approuver la levée
                </button>
              </div>
            </section>
            {message && (
              <p className="encadre ok" role="status">
                {message}
              </p>
            )}
            {!!erreur && <Erreur erreur={erreur} titre="Rien n’a été enregistré." />}
          </div>
        );
      }}
    </Attendre>
  );
}

function Simulation({ r }: { r: Record<string, unknown> }) {
  const etapes = (r.etapes ?? []) as { id: string; libelle: string; raisons?: string[]; bloquee?: boolean }[];
  const prevenus = (r.prevenus ?? []) as { etape: string; nom?: string | null; sub?: string }[];
  return (
    <div className="pile" data-testid="simulation">
      <ul className="liste">
        {etapes.map((e) => (
          <li key={e.id}>
            <b>{e.libelle}</b>
            {e.bloquee ? ' (bloquée : rôle vide)' : ''} — {(e.raisons ?? []).join(' ; ') || 'toujours'}
          </li>
        ))}
      </ul>
      <p>
        Seraient prévenus : {prevenus.length ? prevenus.map((x) => `${x.nom ?? x.sub ?? '?'} (${x.etape})`).join(', ') : 'personne (aucun membre vu ces 30 derniers jours)'}
      </p>
    </div>
  );
}
